import { Spin, Table, Button, Modal, Select, Space, message } from "antd";
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Locale } from "../../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../../utils/helpers/helpers";
import { getList, remove } from "../../../utils/http/dataProvider";
import {
  batchUpDownAlbums,
  batchDeleteAlbums,
  batchSetCategoryAlbums,
} from "../../../utils/http/albumDataProvider";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import { BreadCrumb } from "../../../components/main/LayoutComponents";

import "./AlbumList.scss";
import { albumListColumns } from "./albumListColumns";
import AlbumListHeader from "./AlbumListHeader";

const i18n = Locale.getInstance();
const { Option } = Select;

const AlbumList = () => {
  const user = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState();
  const [categoryId, setCategoryId] = useState(0);
  const [status, setStatus] = useState(undefined);
  const [notForSaleFilter, setNotForSaleFilter] = useState(undefined);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchCategoryModalVisible, setBatchCategoryModalVisible] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(undefined);
  const history = useHistory();
  const [perPage, setPerPage] = useState(10);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [keyword, categoryId, status, notForSaleFilter]);

  const { data, error, isLoading } = useQuery(
    [
      "course-albums",
      user.token,
      page,
      perPage,
      keyword ?? "",
      categoryId,
      status ?? null,
      notForSaleFilter ?? null,
    ],
    () => {
      const filter = {};
      if (keyword) filter.album_name = keyword;
      if (categoryId) filter.category_id = categoryId;
      if (status !== undefined) filter.status = status;
      if (notForSaleFilter === 0 || notForSaleFilter === 1) {
        filter.not_for_sale = notForSaleFilter;
      }
      return getList("course/albums", {
        pagination: {
          perPage,
          page,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        filter,
        token: user.token,
      });
    }
  );

  if (error) {
    showNotificationWithIcon(error.data?.message || error.message);
  }

  const { data: categoriesData } = useQuery(
    ["course-categories", user.token],
    () =>
      getList("course/categories", {
        pagination: {
          perPage: 1000,
          page: 1,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        token: user.token,
      })
  );

  const flattenCategories = (categories, result = []) => {
    if (!categories) return result;
    categories.forEach((cat) => {
      result.push(cat);
      if (cat.sub_categories && cat.sub_categories.length > 0) {
        flattenCategories(cat.sub_categories, result);
      }
    });
    return result;
  };

  const categories = categoriesData?.data ? flattenCategories(categoriesData.data) : [];

  const deleteMutation = useMutation(remove, {
    onSuccess: () => {
      queryClient.invalidateQueries("course-albums");
      setLoading(false);
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
    },
    onError: (error) => {
      setLoading(false);
      handleMutationError(error);
    },
  });

  const batchUpDownMutation = useMutation(
    ({ albumIds, status }) => batchUpDownAlbums(albumIds, status, user.token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("course-albums");
        setSelectedRowKeys([]);
        setLoading(false);
        showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  const batchDeleteMutation = useMutation(
    (albumIds) => batchDeleteAlbums(albumIds, user.token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("course-albums");
        setSelectedRowKeys([]);
        setLoading(false);
        showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  const batchSetCategoryMutation = useMutation(
    ({ albumIds, categoryId }) => batchSetCategoryAlbums(albumIds, categoryId, user.token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("course-albums");
        setSelectedRowKeys([]);
        setLoading(false);
        setBatchCategoryModalVisible(false);
        setSelectedCategoryId(undefined);
        showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  const handleBatchOnShelf = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("course.album.batchSelectWarning"));
      return;
    }
    setLoading(true);
    batchUpDownMutation.mutate({ albumIds: selectedRowKeys, status: 1 });
  };

  const handleBatchOffShelf = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("course.album.batchSelectWarning"));
      return;
    }
    setLoading(true);
    batchUpDownMutation.mutate({ albumIds: selectedRowKeys, status: 0 });
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("course.album.batchSelectWarning"));
      return;
    }
    Modal.confirm({
      title: i18n.t("sales.deleteWarning"),
      content: i18n.t("course.album.selectedCount", { count: selectedRowKeys.length }),
      onOk: () => {
        setLoading(true);
        batchDeleteMutation.mutate(selectedRowKeys);
      },
      okText: i18n.t("base.modal.okText"),
      cancelText: i18n.t("base.modal.cancelText"),
    });
  };

  const handleBatchSetCategory = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("course.album.batchSelectWarning"));
      return;
    }
    setBatchCategoryModalVisible(true);
  };

  const handleBatchCategoryConfirm = () => {
    if (!selectedCategoryId) {
      message.warning(i18n.t("course.album.selectCategoryWarning"));
      return;
    }
    setLoading(true);
    batchSetCategoryMutation.mutate({
      albumIds: selectedRowKeys,
      categoryId: selectedCategoryId,
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const breadCrumbData = [
    {
      href: "/main/courses",
      title: i18n.t("course.title"),
    },
    {
      href: "/main/courses/albums",
      title: i18n.t("course.album.title"),
    },
  ];

  return (
    <>
      <BreadCrumb data={breadCrumbData} />
      <Spin spinning={loading || isLoading}>
        <AlbumListHeader
          keyword={keyword}
          setKeyword={setKeyword}
          categoryId={categoryId}
          setCategoryId={setCategoryId}
          status={status}
          setStatus={setStatus}
          notForSaleFilter={notForSaleFilter}
          setNotForSaleFilter={setNotForSaleFilter}
        />
        <div style={{ marginBottom: 16, marginTop: 16 }}>
          <Space>
            <Button
              onClick={handleBatchOnShelf}
              disabled={selectedRowKeys.length === 0}
            >
              {i18n.t("course.album.batchOnShelf")}
            </Button>
            <Button
              onClick={handleBatchOffShelf}
              disabled={selectedRowKeys.length === 0}
            >
              {i18n.t("course.album.batchOffShelf")}
            </Button>
            <Button
              onClick={handleBatchSetCategory}
              disabled={selectedRowKeys.length === 0}
            >
              {i18n.t("course.album.batchSetCategory")}
            </Button>
            <Button
              onClick={handleBatchDelete}
              disabled={selectedRowKeys.length === 0}
              danger
            >
              {i18n.t("course.album.batchDelete")}
            </Button>
            {selectedRowKeys.length > 0 && (
              <span style={{ marginLeft: 8 }}>
                {i18n.t("course.album.selectedCount", { count: selectedRowKeys.length })}
              </span>
            )}
          </Space>
        </div>
        <Table
          className="list"
          rowKey="id"
          rowSelection={rowSelection}
          columns={albumListColumns(deleteMutation, user.token, setLoading, history)}
          dataSource={data?.data}
          scroll={{
            y: 600,
          }}
          pagination={{
            current: page,
            total: data?.total,
            pageSize: perPage,
            onChange: (newPage) => {
              setPage(newPage);
            },
            onShowSizeChange: (current, size) => {
              setPerPage(size);
              setPage(1);
            },
            pageSizeOptions: ["10", "20", "50", "100"],
            showSizeChanger: true,
          }}
        />
      </Spin>
      <Modal
        title={i18n.t("course.album.batchSetCategory")}
        open={batchCategoryModalVisible}
        onOk={handleBatchCategoryConfirm}
        onCancel={() => {
          setBatchCategoryModalVisible(false);
          setSelectedCategoryId(undefined);
        }}
        okText={i18n.t("base.modal.okText")}
        cancelText={i18n.t("base.modal.cancelText")}
      >
        <Select
          style={{ width: "100%" }}
          placeholder={i18n.t("course.selectCategory")}
          value={selectedCategoryId}
          onChange={(value) => setSelectedCategoryId(value)}
        >
          {categories.map((cat) => (
            <Option key={cat.id} value={cat.id}>
              {i18n.getPropNameByLocale(cat, "name")}
            </Option>
          ))}
        </Select>
      </Modal>
    </>
  );
};

export default AlbumList;




